/**
 * Installation buttons for the app:
 * 
 * 1. **Quick shortcut (Telegram)**: Uses `addToHomeScreen()` API (Bot API 8.0+)
 *    - Creates a shortcut that opens Mini App INSIDE Telegram
 *    - Fast, one-tap installation
 *    - Requires Telegram app
 * 
 * 2. **Full PWA (Browser)**: Opens /install in external browser
 *    - Creates a standalone PWA that works WITHOUT Telegram
 *    - Works offline, push notifications, etc.
 *    - Requires manual "Add to Home Screen" in browser
 * 
 * In Mini App: Shows both options (if supported)
 * In Browser: Shows native install prompt or manual instructions
 */

/**
 * BeforeInstallPromptEvent - fired when the browser is ready to show
 * the install prompt for a PWA. This is a non-standard event that
 * is supported in Chromium-based browsers.
 * 
 * @see https://developer.mozilla.org/en-US/docs/Web/API/BeforeInstallPromptEvent
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getTelegram } from "../lib/telegram.ts";
import { isTelegramMiniApp } from "../lib/telegram.ts";
import { pwaLog } from "../lib/logger.ts";
import { haptic } from "../lib/telegram.ts";
import { useSafeTimeout } from "../lib/hooks";
import { 
  supportsAddToHomeScreen, 
  addMiniAppToHomeScreen,
  checkMiniAppHomeScreenStatus,
  type HomeScreenStatus 
} from "../lib/telegram.ts";
import { IconPlus, IconChevronRight } from "./Icons.tsx";
import { useAuth } from "../lib/auth.tsx";


/**
 * Button to add Mini App shortcut via Telegram's native API
 * This creates a shortcut that opens inside Telegram (not standalone PWA)
 * Requires email + password before adding (same as PWA install).
 */
export function AddToHomeScreenButton() {
  const { account } = useAuth();
  const navigate = useNavigate();
  const { setTimeout } = useSafeTimeout();
  const [homeScreenStatus, setHomeScreenStatus] = useState<HomeScreenStatus | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const tg = getTelegram();

  // Check if already added to home screen
  useEffect(() => {
    if (!supportsAddToHomeScreen()) return;
    
    checkMiniAppHomeScreenStatus()
      .then(status => {
        setHomeScreenStatus(status);
      })
      .catch((err) => {
        pwaLog.debug("Failed to check home screen status:", err);
      });

    // Listen for homeScreenAdded event
    const handleAdded = () => {
      setJustAdded(true);
      setHomeScreenStatus("added");
      haptic("success");
    };

    // Subscribe to Telegram events
    if (tg?.onEvent) {
      tg.onEvent("homeScreenAdded", handleAdded);
    }

    return () => {
      if (tg?.offEvent) {
        tg.offEvent("homeScreenAdded", handleAdded);
      }
    };
  }, [tg]);

  const handleClick = useCallback(() => {
    haptic("light");
    if (!account?.email) {
      navigate("/profile/email");
      return;
    }
    if (!account?.hasPassword) {
      navigate("/profile/password");
      return;
    }
    setIsAdding(true);
    try {
      addMiniAppToHomeScreen();
    } finally {
      setTimeout(() => setIsAdding(false), 3000);
    }
  }, [account?.email, account?.hasPassword, navigate, setTimeout]);

  // Don't show if not supported or already added
  if (!supportsAddToHomeScreen()) return null;
  if (homeScreenStatus === "added" || justAdded) {
    return (
      <div className="block w-full card p-3 xs:p-4 md-mobile:p-5 lg-mobile:p-6 text-left opacity-60">
        <div className="flex items-center gap-2.5 xs:gap-3 md-mobile:gap-4 lg-mobile:gap-5">
          <div className="flex-shrink-0 w-9 h-9 xs:w-10 xs:h-10 md-mobile:w-12 md-mobile:h-12 lg-mobile:w-14 lg-mobile:h-14 rounded-lg xs:rounded-xl lg-mobile:rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600">
            <svg className="w-5 h-5 xs:w-6 xs:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-slate-900 text-sm xs:text-base md-mobile:text-lg lg-mobile:text-xl">
              Ярлык добавлен
            </p>
            <p className="text-xs xs:text-sm md-mobile:text-base lg-mobile:text-lg text-slate-500 mt-0.5">
              Откройте с главного экрана
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isAdding}
      className="block w-full card p-3 xs:p-4 md-mobile:p-5 lg-mobile:p-6 hover:border-amber-400 hover:shadow-md transition-all active:scale-[0.99] group text-left disabled:opacity-70 disabled:cursor-wait"
    >
      <div className="flex items-center gap-2.5 xs:gap-3 md-mobile:gap-4 lg-mobile:gap-5">
        <div className="flex-shrink-0 w-9 h-9 xs:w-10 xs:h-10 md-mobile:w-12 md-mobile:h-12 lg-mobile:w-14 lg-mobile:h-14 rounded-lg xs:rounded-xl lg-mobile:rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600 group-hover:bg-amber-100 transition-colors">
          <IconPlus className="w-5 h-5 xs:w-6 xs:h-6 md-mobile:w-7 md-mobile:h-7 lg-mobile:w-8 lg-mobile:h-8" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-slate-900 text-sm xs:text-base md-mobile:text-lg lg-mobile:text-xl">
            {isAdding ? "Добавление..." : "Добавить ярлык"}
          </p>
          <p className="text-xs xs:text-sm md-mobile:text-base lg-mobile:text-lg text-slate-500 mt-0.5">
            Быстрый доступ с главного экрана
          </p>
        </div>
        <span className="text-slate-400 group-hover:text-slate-600 transition-colors text-lg md-mobile:text-xl lg-mobile:text-2xl">
          →
        </span>
      </div>
    </button>
  );
}

const cardStyleClasses = {
  button:
    "flex items-center gap-3 p-4 rounded-2xl bg-white/95 shadow-sm active:bg-slate-50 transition-colors min-h-[56px] w-full text-left disabled:opacity-70 disabled:cursor-wait no-underline text-inherit",
  icon: "w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 shrink-0",
  label: "font-medium text-[15px] text-slate-800 block truncate",
  description: "text-[13px] text-slate-500 block truncate",
};

/**
 * Button to install full PWA via external browser
 * This creates a standalone app that works without Telegram
 */
export function InstallPWAButton({ cardStyle = false }: { cardStyle?: boolean } = {}) {
  const navigate = useNavigate();
  const { account } = useAuth();
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [isPrompting, setIsPrompting] = useState(false);
  const isMiniApp = isTelegramMiniApp();
  const tg = getTelegram();

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Hide when already running as standalone PWA
  useEffect(() => {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as { standalone?: boolean }).standalone === true;
    if (isStandalone) setInstalled(true);
  }, []);

  // Proceed with PWA installation
  // User will need to login via magic link in PWA (simpler, more secure)
  const proceedWithInstall = useCallback(() => {
    if (isMiniApp && tg?.openLink) {
      // Open install page in external browser
      tg.openLink(`${window.location.origin}/install`);
      return;
    }
    navigate("/install");
  }, [isMiniApp, tg, navigate]);

  const handleClick = useCallback(async () => {
    haptic("light");
    if (deferredPrompt) {
      setIsPrompting(true);
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") setInstalled(true);
        setDeferredPrompt(null);
      } catch {
        setDeferredPrompt(null);
      } finally {
        setIsPrompting(false);
      }
      return;
    }
    // Check email/password before PWA install (for magic link login)
    if (!account?.email) {
      navigate("/profile/email");
      return;
    }
    if (!account?.hasPassword) {
      navigate("/profile/password");
      return;
    }
    proceedWithInstall();
  }, [deferredPrompt, account?.email, account?.hasPassword, proceedWithInstall, navigate]);

  const show =
    !installed &&
    (deferredPrompt || (isMiniApp && tg?.openLink) || !isMiniApp);

  if (!show) return null;

  const hint = deferredPrompt
    ? "Добавить на главный экран"
    : isMiniApp
      ? "Откроется в браузере — войдите по email"
      : "Работает без Telegram и интернета";

  if (cardStyle) {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={isPrompting}
        className={cardStyleClasses.button}
      >
        <div className={cardStyleClasses.icon}>
          <IconPlus className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <span className={cardStyleClasses.label}>Установить приложение</span>
          <span className={cardStyleClasses.description}>{hint}</span>
        </div>
        <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isPrompting}
      className="block w-full card p-3 xs:p-4 md-mobile:p-5 lg-mobile:p-6 hover:border-amber-400 hover:shadow-md transition-all active:scale-[0.99] group text-left disabled:opacity-70 disabled:cursor-wait"
    >
      <div className="flex items-center gap-2.5 xs:gap-3 md-mobile:gap-4 lg-mobile:gap-5">
        <div className="flex-shrink-0 w-9 h-9 xs:w-10 xs:h-10 md-mobile:w-12 md-mobile:h-12 lg-mobile:w-14 lg-mobile:h-14 rounded-lg xs:rounded-xl lg-mobile:rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600 group-hover:bg-amber-100 transition-colors">
          <IconPlus className="w-5 h-5 xs:w-6 xs:h-6 md-mobile:w-7 md-mobile:h-7 lg-mobile:w-8 lg-mobile:h-8" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-slate-900 text-sm xs:text-base md-mobile:text-lg lg-mobile:text-xl">
            Установить приложение
          </p>
          <p className="text-xs xs:text-sm md-mobile:text-base lg-mobile:text-lg text-slate-500 mt-0.5">
            {hint}
          </p>
        </div>
        <span className="text-slate-400 group-hover:text-slate-600 transition-colors text-lg md-mobile:text-xl lg-mobile:text-2xl">
          →
        </span>
      </div>
    </button>
  );
}

/**
 * Installation section (profile/settings).
 * Shows full PWA installation only (no "Add shortcut").
 * cardStyle: same card look as profile sections (rounded-2xl, icon 12x12, chevron).
 */
export function InstallSection({ cardStyle = false }: { cardStyle?: boolean } = {}) {
  return (
    <div className={cardStyle ? "" : "space-y-2"}>
      <InstallPWAButton cardStyle={cardStyle} />
    </div>
  );
}

/**
 * Compact banner for installing PWA from Mini App
 * Opens the install page in external browser where user can add real PWA
 * 
 * NOTE: Telegram's addToHomeScreen() only adds a link shortcut, NOT a PWA.
 * For real PWA installation, we must open the page in Safari/Chrome.
 */
const BANNER_DISMISS_KEY = "pwa-install-banner-dismissed";

export function AddToHomeScreenBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [homeScreenStatus, setHomeScreenStatus] = useState<HomeScreenStatus | null>(null);
  const tg = getTelegram();
  const isMiniApp = isTelegramMiniApp();

  useEffect(() => {
    // Only show in Mini App
    if (!isMiniApp) return;

    // Check if already added to home screen via Telegram API (Bot API 8.0+)
    if (supportsAddToHomeScreen()) {
      checkMiniAppHomeScreenStatus()
        .then(status => {
          setHomeScreenStatus(status);
          // If already added via Telegram, don't show banner
          if (status === "added") {
            setIsVisible(false);
            return;
          }
        })
        .catch((err) => {
          pwaLog.debug("Failed to check home screen status:", err);
        });
    }

    // Check if banner was dismissed by user
    try {
      const dismissedAt = localStorage.getItem(BANNER_DISMISS_KEY);
      if (dismissedAt) {
        // Don't show again (permanent dismiss)
        setDismissed(true);
        return;
      }
    } catch {
      // ignore
    }

    setIsVisible(true);
  }, [isMiniApp]);

  // Listen for homeScreenAdded event to hide banner
  useEffect(() => {
    if (!isMiniApp || !supportsAddToHomeScreen()) return;

    const handleAdded = () => {
      setHomeScreenStatus("added");
      setIsVisible(false);
    };

    if (tg?.onEvent) {
      tg.onEvent("homeScreenAdded", handleAdded);
    }

    return () => {
      if (tg?.offEvent) {
        tg.offEvent("homeScreenAdded", handleAdded);
      }
    };
  }, [isMiniApp, tg]);

  const handleInstall = useCallback(() => {
    if (!tg?.openLink) return;
    
    haptic("light");
    
    // Open install page in external browser
    // User will login via magic link (simpler, more secure)
    tg.openLink(`${window.location.origin}/install`);
  }, [tg]);

  const handleDismiss = useCallback(() => {
    try {
      localStorage.setItem(BANNER_DISMISS_KEY, Date.now().toString());
    } catch {
      // ignore
    }
    setDismissed(true);
    setIsVisible(false);
  }, []);

  // Don't show if dismissed, already added, or not in Mini App
  if (dismissed || !isVisible || homeScreenStatus === "added") {
    return null;
  }

  return (
    <div
      className="rounded-xl p-3 shadow-lg lg:p-2 lg:rounded-lg"
      style={{
        background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      }}
    >
      <div className="flex items-center gap-3 lg:gap-2">
        {/* Icon */}
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center lg:w-8 lg:h-8 lg:rounded-md">
          <svg className="w-5 h-5 text-white lg:w-4 lg:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </div>
        
        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium text-sm lg:text-xs">
            Установить приложение
          </p>
          <p className="text-white/70 text-xs mt-0.5 lg:text-[11px] lg:mt-0">
            Работает без Telegram и интернета
          </p>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0 lg:gap-1">
          <button
            type="button"
            onClick={handleDismiss}
            className="p-1.5 text-white/60 hover:text-white/90 transition-colors lg:p-1"
            aria-label="Закрыть"
          >
            <svg className="w-4 h-4 lg:w-3.5 lg:h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleInstall}
            className="px-3 py-1.5 bg-white text-amber-700 rounded-lg text-sm font-semibold hover:bg-white/90 transition-colors lg:px-2 lg:py-1 lg:text-xs lg:rounded-md"
          >
            Установить
          </button>
        </div>
      </div>
    </div>
  );
}
